import { z } from 'zod';
import { normalizeText } from './importer';
import { expenseCategorySchema } from './validation';
import type { Ingredient, Supplier, SupplierKind } from './types';

/*
 * Proveedores de verdad (fase 7, 12 oct 2026). Reglas puras que comparten la
 * API, la demo y la pantalla: contacto separado, días de pedido y entrega,
 * tipo deducido, repetidos y el mismo producto con varios proveedores.
 */

/* ------------------------------------------------------------------ */
/* Entrada                                                             */
/* ------------------------------------------------------------------ */

const dia = z.number().int().min(1).max(7);
const dias = z
  .array(dia)
  .max(7)
  .nullable()
  .optional()
  .transform((lista) => (lista ? [...new Set(lista)].sort((a, b) => a - b) : lista));

/**
 * Lo que el usuario puede escribir de un proveedor. `archived` y
 * `distinctFrom` NO están: se cambian con sus acciones (archivar, «No, son
 * distintos», unir) para que guardar la ficha no los pise.
 *
 * En TODOS los campos (salvo el nombre): ausente = se queda el que había;
 * vacío (`''`, `[]`, `false`) o `null` = borrarlo. La app del celular manda
 * solo nombre, contacto y notas, y no puede borrar el teléfono ni los días.
 */
export const supplierInputSchema = z.object({
  name: z.string().trim().min(1, 'El nombre es obligatorio').max(120),
  contact: z.string().max(160).nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
  contactName: z.string().trim().max(120).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  email: z.string().trim().max(160).nullable().optional(),
  taxId: z.string().trim().max(30).nullable().optional(),
  orderDays: dias,
  orderCutoff: z
    .union([z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'La hora límite va como 18:00'), z.literal('')])
    .nullable()
    .optional(),
  deliveryDays: dias,
  orderReminder: z.boolean().nullable().optional(),
  kind: z.enum(['cocina', 'otros']).or(z.literal('')).nullable().optional(),
  /* Bloque G (14 oct 2026): lo que el proveedor recuerda para el siguiente gasto. */
  creditDays: z.number().int().min(0).max(365).nullable().optional(),
  vatRatio: z.number().min(0).max(0.5).nullable().optional(),
  lastCategory: expenseCategorySchema.nullable().optional(),
  lastSubgroup: z.string().trim().max(40).nullable().optional(),
  billing: z.enum(['cada_vez', 'mensual', 'nunca']).nullable().optional(),
});

export type SupplierInputParsed = z.infer<typeof supplierInputSchema>;

/**
 * Aplica un guardado de la ficha sobre el proveedor que había. Campo ausente
 * = se conserva; vacío o `null` = se borra (ver `supplierInputSchema`). Los
 * de sistema (archivado, «son distintos») siempre se conservan.
 */
export function aplicarEdicionProveedor(
  current: Supplier,
  input: SupplierInputParsed,
): Supplier {
  /** `undefined` = el actual; `null`/vacío = nada; si no, el nuevo. */
  const campo = <T>(nuevo: T | null | undefined, actual: T | undefined, vacio: (v: T) => boolean): T | undefined => {
    if (nuevo === undefined) return actual;
    if (nuevo === null || vacio(nuevo)) return undefined;
    return nuevo;
  };
  const texto = (v: string) => v.trim() === '';
  const lista = (v: number[]) => v.length === 0;
  const next: Supplier = {
    tenantId: current.tenantId,
    supplierId: current.supplierId,
    name: input.name,
    contact: campo(input.contact, current.contact, texto),
    notes: campo(input.notes, current.notes, texto),
    contactName: campo(input.contactName, current.contactName, texto),
    phone: campo(input.phone, current.phone, texto),
    email: campo(input.email, current.email, texto),
    taxId: campo(input.taxId == null ? input.taxId : taxIdComparable(input.taxId) ?? '', current.taxId, texto),
    orderDays: campo(input.orderDays, current.orderDays, lista),
    orderCutoff: campo(input.orderCutoff, current.orderCutoff, texto),
    deliveryDays: campo(input.deliveryDays, current.deliveryDays, lista),
    orderReminder: campo(input.orderReminder, current.orderReminder, (v) => !v),
    kind: campo(input.kind, current.kind, (v) => v === '') as SupplierKind | undefined,
    creditDays: campo(input.creditDays, current.creditDays, () => false),
    vatRatio: campo(input.vatRatio, current.vatRatio, () => false),
    lastCategory: campo(input.lastCategory, current.lastCategory, () => false),
    lastSubgroup: campo(input.lastSubgroup, current.lastSubgroup, texto),
    billing: campo(input.billing, current.billing, () => false),
    archived: current.archived,
    distinctFrom: current.distinctFrom,
  };
  return sinVacios(next);
}

/** Alta: lo mismo que una edición sobre un proveedor vacío. */
export function nuevoProveedor(tenantId: string, supplierId: string, input: SupplierInputParsed): Supplier {
  return aplicarEdicionProveedor({ tenantId, supplierId, name: input.name }, input);
}

function sinVacios<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

/* ------------------------------------------------------------------ */
/* Contacto                                                            */
/* ------------------------------------------------------------------ */

const RE_EMAIL = /[^\s@,;<>()]+@[^\s@,;<>()]+\.[^\s@,;<>()]+/;
const RE_TELEFONO = /\+?\d[\d\s().-]{6,}\d/;

export interface ContactoProveedor {
  persona?: string;
  telefono?: string;
  email?: string;
}

/**
 * Persona, teléfono y email. Los proveedores de antes solo tienen el campo
 * libre «Contacto»: si los campos nuevos están vacíos, se reparte al leer
 * (lo que parece email va a email, lo que parece teléfono a teléfono y el
 * resto es la persona). No se reescribe nada hasta que el usuario guarda.
 */
export function contactoDeProveedor(s: Pick<Supplier, 'contact' | 'contactName' | 'phone' | 'email'>): ContactoProveedor {
  const out: ContactoProveedor = {
    persona: s.contactName?.trim() || undefined,
    telefono: s.phone?.trim() || undefined,
    email: s.email?.trim() || undefined,
  };
  const libre = s.contact?.trim();
  if (!libre) return out;
  let resto = libre;
  const email = resto.match(RE_EMAIL)?.[0];
  if (email) resto = resto.replace(email, ' ');
  const tel = resto.match(RE_TELEFONO)?.[0];
  if (tel && tel.replace(/\D/g, '').length >= 8) resto = resto.replace(tel, ' ');
  const persona = resto.replace(/[\s,;·|/-]+/g, ' ').trim();
  return {
    persona: out.persona ?? (persona || undefined),
    telefono: out.telefono ?? (tel && tel.replace(/\D/g, '').length >= 8 ? tel.trim() : undefined),
    email: out.email ?? email,
  };
}

/** Solo dígitos, para comparar teléfonos escritos de mil maneras. */
export function telefonoComparable(tel: string | undefined): string | undefined {
  const d = (tel ?? '').replace(/\D/g, '');
  if (d.length < 8) return undefined;
  // Los últimos 9 dígitos: «+52 668 412 2070» y «668 412 2070» son el mismo.
  return d.slice(-9);
}

/** Mayúsculas y sin separadores; el prefijo de país europeo (ES…) fuera. */
export function taxIdComparable(taxId: string | undefined): string | undefined {
  const t = (taxId ?? '').toUpperCase().replace(/[^A-Z0-9Ñ&]/g, '');
  if (!t) return undefined;
  return /^ES[A-Z0-9]\d{7}[A-Z0-9]$/.test(t) ? t.slice(2) : t;
}

/* ------------------------------------------------------------------ */
/* Nombre                                                              */
/* ------------------------------------------------------------------ */

/** Formas jurídicas que no distinguen a un proveedor de otro. */
const FORMAS = new Set([
  'sa', 'sl', 'slu', 'sll', 'sc', 'cv', 'de', 'rl', 'sapi', 'sab', 'srl', 'sas', 'spa', 'ltda', 'ltd',
  'inc', 'llc', 'corp', 'co', 'cb', 'scp', 'coop', 'gmbh', 'eirl', 'sacv', 'sadecv', 'srldecv', 'y', 'cia',
]);

/**
 * Nombre comparable: «CARNES ROMA, S.A. DE C.V.» → «carnes roma». Sin tildes,
 * sin signos y sin la forma jurídica del final.
 */
export function normalizarNombreProveedor(name: string): string {
  const palabras = normalizeText(name.replace(/\./g, '')).split(' ').filter(Boolean);
  while (palabras.length > 1 && FORMAS.has(palabras[palabras.length - 1])) palabras.pop();
  return palabras.join(' ');
}

function distancia(a: string, b: string): number {
  if (a === b) return 0;
  const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let diag = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j];
      prev[j] = Math.min(prev[j] + 1, prev[j - 1] + 1, diag + (a[i - 1] === b[j - 1] ? 0 : 1));
      diag = tmp;
    }
  }
  return prev[b.length];
}

/** «Carnes Roma» ~ «CARNES ROMA SA DE CV» ~ «Carnes Romma». */
export function nombresParecidos(a: string, b: string): boolean {
  const x = normalizarNombreProveedor(a);
  const y = normalizarNombreProveedor(b);
  if (!x || !y) return false;
  if (x === y) return true;
  const corto = x.length <= y.length ? x : y;
  const largo = corto === x ? y : x;
  // Contenido como palabras enteras y con cuerpo: «roma» no casa con «aroma».
  if (corto.length >= 5 && ` ${largo} `.includes(` ${corto} `)) return true;
  if (corto.length >= 6) return distancia(x, y) <= (corto.length >= 14 ? 2 : 1);
  return false;
}

/* ------------------------------------------------------------------ */
/* Repetidos                                                           */
/* ------------------------------------------------------------------ */

export type MotivoRepetido = 'taxId' | 'telefono' | 'nombre';

export interface PosibleRepetido {
  a: Supplier;
  b: Supplier;
  motivo: MotivoRepetido;
}

/**
 * Parejas que pueden ser el mismo proveedor: mismo ID fiscal, mismo teléfono
 * o nombre parecido. Fuera los archivados y las parejas que el usuario ya
 * dijo que son distintas. Cada proveedor sale como mucho en una pareja, para
 * que el aviso de arriba no se convierta en una lista.
 */
export function posiblesRepetidos(suppliers: Supplier[]): PosibleRepetido[] {
  const vivos = suppliers.filter((s) => !s.archived);
  const out: PosibleRepetido[] = [];
  const usados = new Set<string>();
  const distintos = (a: Supplier, b: Supplier) =>
    Boolean(a.distinctFrom?.includes(b.supplierId) || b.distinctFrom?.includes(a.supplierId));
  for (let i = 0; i < vivos.length; i++) {
    for (let j = i + 1; j < vivos.length; j++) {
      const a = vivos[i];
      const b = vivos[j];
      if (usados.has(a.supplierId) || usados.has(b.supplierId) || distintos(a, b)) continue;
      const motivo = motivoRepetido(a, b);
      if (!motivo) continue;
      out.push({ a, b, motivo });
      usados.add(a.supplierId);
      usados.add(b.supplierId);
    }
  }
  return out;
}

export function motivoRepetido(a: Supplier, b: Supplier): MotivoRepetido | null {
  const ta = taxIdComparable(a.taxId);
  const tb = taxIdComparable(b.taxId);
  if (ta && tb) {
    if (ta === tb) return 'taxId';
    // Dos ID fiscales distintos: son dos empresas aunque se llamen igual.
    return null;
  }
  const fa = telefonoComparable(contactoDeProveedor(a).telefono);
  const fb = telefonoComparable(contactoDeProveedor(b).telefono);
  if (fa && fb && fa === fb) return 'telefono';
  if (nombresParecidos(a.name, b.name)) return 'nombre';
  return null;
}

export function fraseRepetido(r: PosibleRepetido, etiquetaTaxId = 'ID fiscal'): string {
  const que =
    r.motivo === 'taxId'
      ? `tienen el mismo ${etiquetaTaxId}`
      : r.motivo === 'telefono'
        ? 'tienen el mismo teléfono'
        : 'se llaman casi igual';
  return `«${r.a.name}» y «${r.b.name}» ${que}.`;
}

/**
 * Lo que queda del proveedor que se queda tras unir: sus datos mandan y los
 * huecos se rellenan con los del otro. Las notas se juntan.
 */
export function proveedorUnido(queda: Supplier, sale: Supplier): Supplier {
  const notas = [queda.notes, sale.notes].map((n) => n?.trim()).filter(Boolean);
  const distinct = [...new Set([...(queda.distinctFrom ?? []), ...(sale.distinctFrom ?? [])])].filter(
    (id) => id !== queda.supplierId && id !== sale.supplierId,
  );
  return sinVacios({
    ...sale,
    ...sinVacios(queda),
    tenantId: queda.tenantId,
    supplierId: queda.supplierId,
    name: queda.name,
    notes: notas.length ? [...new Set(notas)].join('\n').slice(0, 500) : undefined,
    archived: queda.archived,
    distinctFrom: distinct.length ? distinct : undefined,
  });
}

/* ------------------------------------------------------------------ */
/* Tipo                                                                */
/* ------------------------------------------------------------------ */

/**
 * De cocina (te vende ingredientes) u Otros gastos. Lo pone Alexia sola; la
 * corrección manual de «Más opciones» manda.
 */
export function tipoDeProveedor(s: Pick<Supplier, 'kind'>, ingredientes: number): SupplierKind {
  return s.kind ?? (ingredientes > 0 ? 'cocina' : 'otros');
}

/* ------------------------------------------------------------------ */
/* Pedido y entrega                                                    */
/* ------------------------------------------------------------------ */

export const DIAS_SEMANA: Array<{ dia: number; nombre: string; corto: string }> = [
  { dia: 1, nombre: 'lunes', corto: 'lun' },
  { dia: 2, nombre: 'martes', corto: 'mar' },
  { dia: 3, nombre: 'miércoles', corto: 'mié' },
  { dia: 4, nombre: 'jueves', corto: 'jue' },
  { dia: 5, nombre: 'viernes', corto: 'vie' },
  { dia: 6, nombre: 'sábado', corto: 'sáb' },
  { dia: 7, nombre: 'domingo', corto: 'dom' },
];

/** Día ISO (1 = lunes … 7 = domingo) de una fecha «AAAA-MM-DD». */
export function diaIso(fecha: string): number {
  const [y, m, d] = fecha.split('-').map(Number);
  const js = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return js === 0 ? 7 : js;
}

function listaDias(dias: number[]): string {
  const nombres = dias.map((d) => DIAS_SEMANA[d - 1]?.nombre).filter(Boolean);
  if (nombres.length <= 1) return nombres.join('');
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

/** «Pide jueves antes de 18:00 · entrega viernes». Vacío si no hay días. */
export function frasePedidoEntrega(s: Pick<Supplier, 'orderDays' | 'orderCutoff' | 'deliveryDays'>): string {
  const partes: string[] = [];
  if (s.orderDays?.length) {
    partes.push(`Pide ${listaDias(s.orderDays)}${s.orderCutoff ? ` antes de ${s.orderCutoff}` : ''}`);
  }
  if (s.deliveryDays?.length) partes.push(`${partes.length ? 'entrega' : 'Entrega'} ${listaDias(s.deliveryDays)}`);
  return partes.join(' · ');
}

/**
 * Proveedores a los que toca pedir hoy y pidieron el recordatorio. Es lo que
 * sale en «Para hoy» del Panel.
 */
export function pedidosDeHoy(suppliers: Supplier[], fecha: string): Supplier[] {
  const hoy = diaIso(fecha);
  return suppliers
    .filter((s) => !s.archived && s.orderReminder && s.orderDays?.includes(hoy))
    .sort((a, b) => (a.orderCutoff ?? '99').localeCompare(b.orderCutoff ?? '99') || a.name.localeCompare(b.name, 'es'));
}

/* ------------------------------------------------------------------ */
/* El mismo producto con varios proveedores                            */
/* ------------------------------------------------------------------ */

/**
 * Otros ingredientes que son el mismo producto con otro proveedor. Hoy un
 * ingrediente tiene un solo proveedor, así que «la pechuga de Carnes Roma» y
 * «la pechuga de Costco» son dos fichas con el mismo nombre. Se emparejan
 * por nombre normalizado (sin tildes ni mayúsculas) y proveedor distinto.
 */
export function alternativasDeProveedor(ing: Ingredient, todos: Ingredient[]): Ingredient[] {
  if (!ing.supplierId) return [];
  const nombre = normalizeText(ing.name);
  if (!nombre) return [];
  return todos.filter(
    (o) =>
      o.ingredientId !== ing.ingredientId &&
      o.active !== false &&
      Boolean(o.supplierId) &&
      o.supplierId !== ing.supplierId &&
      normalizeText(o.name) === nombre,
  );
}
